import React from 'react';
import { match } from 'ts-pattern';
import { useLogger } from '#/logger';
import type { FeatureFlag } from '../flags';
import {
  type UseFeatureFlagHook,
  useFeatureFlag,
} from '../hooks/use-feature-flag';

/**
 * A component or function to render if the feature flag is disabled.
 */
export type FeatureToggleNode<F extends FeatureFlag = FeatureFlag> =
  | React.ReactNode
  | RenderFunc<F>;

export interface FeatureToggleProps<F extends FeatureFlag = FeatureFlag> {
  /**
   * The feature flag to check
   */
  flag: F;
  /**
   * A component or function to render if the feature flag is disabled.
   */
  fallback?: FeatureToggleNode<F>;
  /**
   * A component or function if the feature flag is enabled.
   */
  children: FeatureToggleNode<F>;
  /**
   * A component to render while the feature flag is loading.
   */
  loader?: React.ReactElement | null;
}

/**
 * Component for toggling a feature based on a feature flag.
 */
export function FeatureToggle<F extends FeatureFlag>({
  flag,
  children,
  fallback = null,
  loader = null,
}: FeatureToggleProps<F>) {
  const flagResult = useFeatureFlag(flag);
  const logger = useLogger();

  const renderFallback = React.useMemo(() => {
    return getNode(fallback, flagResult);
  }, [flagResult, fallback]);

  const renderChildren = React.useMemo(() => {
    return getNode(children, flagResult);
  }, [flagResult, children]);

  React.useEffect(() => {
    if (flagResult.enabled && fallback === undefined) {
      logger.warn(
        `Feature flag "${flag}" is enabled but the fallback is NOT nil.`
      );
    }
  }, [flagResult.enabled, flag, fallback, logger]);

  return match(flagResult)
    .with({ enabled: true, loading: false }, () => renderChildren)
    .with({ enabled: false, loading: false }, () => renderFallback)
    .with({ loading: true }, () => loader)
    .exhaustive();
}

/*
|------------------
| Internals
|------------------
*/

type RenderFunc<F extends FeatureFlag> = (
  featureFlag: UseFeatureFlagHook<F>
) => React.ReactNode;

function getNode<F extends FeatureFlag>(
  node: FeatureToggleNode<F>,
  flagResult: UseFeatureFlagHook<F>
): React.ReactNode {
  return match(node)
    .when(
      (fb) => isRenderFunc(fb),
      (fn) => fn(flagResult)
    )
    .otherwise((fb) => fb);
}

function isReactNode(node: unknown): node is React.ReactNode {
  return (
    typeof node === 'string' ||
    typeof node === 'number' ||
    typeof node === 'boolean' ||
    typeof node === 'object'
  );
}

function isRenderFunc<F extends FeatureFlag>(fn: unknown): fn is RenderFunc<F> {
  return typeof fn === 'function' && !isReactNode(fn);
}
